import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Drawer,
  FormHelperText,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useSnackbar } from "notistack";
import {
  createEmptyQuestion,
  getMeasureTypeMeta,
  getScaleOptions,
  isScaleFieldType,
  MEASURE_DIRECTIONS,
  MEASURE_TYPES,
  needsOptions,
  normalizeQuestion,
  QUESTION_FIELD_TYPES,
  QUESTION_MODES,
  saveQuestion,
} from "./questionStorage";
import {
  addTrackerQuestionApi,
  updateTrackerQuestionApi,
} from "../../../DAL/trackerQuestion/trackerQuestion";

const PRIMARY = "var(--portal-theme-primary)";
const BORDER = "#e2e8f0";
const MUTED = "#64748b";

const UnitChip = ({ label, selected, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: "inline-block",
      border: "1px solid",
      borderColor: selected ? "primary.main" : "#ddd",
      bgcolor: selected ? "action.hover" : "#fff",
      borderRadius: 1,
      px: 1.25,
      py: 0.5,
      mr: 1,
      mb: 1,
      cursor: "pointer",
      fontSize: 13,
      fontWeight: selected ? 600 : 400,
    }}
  >
    {label}
  </Box>
);

const emptyForm = () => ({
  label: "",
  placeholder: "",
  fieldType: "short_text",
  mode: QUESTION_MODES.GENERAL,
  measureType: "weight",
  units: ["kg", "lbs"],
  defaultUnit: "kg",
  customUnitsText: "",
  measureDirection: MEASURE_DIRECTIONS.ASCENDING,
  scaleMin: 1,
  scaleMax: 10,
  optionsText: "",
});

export default function AddOrEditQuestionDrawer({
  open,
  onClose,
  question,
  onSaved,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = Boolean(question?._id || question?.id);
  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;

    if (question) {
      const presetUnits = getMeasureTypeMeta(question.measureType)?.units || [];
      const savedUnits = Array.isArray(question.units) ? question.units : [];
      const customOnly = savedUnits.filter((unit) => !presetUnits.includes(unit));
      const scaleMin = Number(question.scaleMin) || 1;
      const scaleMax = Number(question.scaleMax) || 10;

      setForm({
        label: question.label || "",
        placeholder: question.placeholder || "",
        fieldType: question.fieldType || "short_text",
        mode: question.mode || QUESTION_MODES.GENERAL,
        measureType: question.measureType || "weight",
        units: savedUnits,
        defaultUnit: question.defaultUnit || savedUnits[0] || "",
        customUnitsText: customOnly.join("\n"),
        measureDirection:
          question.measureDirection || MEASURE_DIRECTIONS.DESCENDING,
        scaleMin,
        scaleMax,
        optionsText: Array.isArray(question.options)
          ? question.options.join("\n")
          : "",
      });
    } else {
      setForm(emptyForm());
    }
    setErrors({});
  }, [open, question]);

  const measureMeta = useMemo(
    () => getMeasureTypeMeta(form.measureType),
    [form.measureType]
  );

  const customUnits = useMemo(
    () =>
      form.customUnitsText
        .split("\n")
        .map((item) => item.trim())
        .filter(Boolean),
    [form.customUnitsText]
  );

  const availableUnits = useMemo(() => {
    const presets = measureMeta?.units || [];
    return [...presets, ...customUnits.filter((unit) => !presets.includes(unit))];
  }, [measureMeta, customUnits]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleFieldTypeChange = (fieldType) => {
    setForm((prev) => {
      const next = { ...prev, fieldType };
      if (isScaleFieldType(fieldType)) {
        next.scaleMin = prev.scaleMin || 1;
        next.scaleMax = prev.scaleMax || 10;
        next.optionsText = getScaleOptions(next.scaleMin, next.scaleMax).join(
          "\n"
        );
        if (!prev.placeholder?.trim()) {
          next.placeholder = `Rate from ${next.scaleMin} to ${next.scaleMax}`;
        }
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, fieldType: "", optionsText: "" }));
  };

  const handleScaleRangeChange = (field, rawValue) => {
    const num = Math.max(0, Math.min(100, Number(rawValue) || 0));
    setForm((prev) => {
      const scaleMin = field === "scaleMin" ? num : prev.scaleMin;
      const scaleMax = field === "scaleMax" ? num : prev.scaleMax;
      const min = Math.min(scaleMin, scaleMax);
      const max = Math.max(scaleMin, scaleMax);
      return {
        ...prev,
        scaleMin: min,
        scaleMax: max,
        optionsText: getScaleOptions(min, max).join("\n"),
      };
    });
    setErrors((prev) => ({ ...prev, scaleMin: "", scaleMax: "" }));
  };

  const handleCustomUnitsChange = (value) => {
    const nextCustom = value
      .split("\n")
      .map((item) => item.trim())
      .filter(Boolean);
    const presets = getMeasureTypeMeta(form.measureType)?.units || [];

    setForm((prev) => {
      const keptPresetSelected = prev.units.filter((unit) =>
        presets.includes(unit)
      );
      const units = [...keptPresetSelected, ...nextCustom];
      const defaultUnit = units.includes(prev.defaultUnit)
        ? prev.defaultUnit
        : units[0] || "";
      return {
        ...prev,
        customUnitsText: value,
        units,
        defaultUnit,
      };
    });
    setErrors((prev) => ({
      ...prev,
      customUnitsText: "",
      units: "",
      defaultUnit: "",
    }));
  };

  const handleModeChange = (mode) => {
    if (mode === QUESTION_MODES.MEASURABLE) {
      const meta = getMeasureTypeMeta("weight");
      setForm((prev) => ({
        ...prev,
        mode,
        fieldType: "number",
        measureType: "weight",
        units: meta?.units || ["kg", "lbs"],
        defaultUnit: meta?.units?.[0] || "kg",
        measureDirection:
          meta?.defaultDirection || MEASURE_DIRECTIONS.ASCENDING,
        customUnitsText: "",
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        mode,
        fieldType: prev.fieldType === "number" ? "short_text" : prev.fieldType,
      }));
    }
    setErrors({});
  };

  const handleMeasureTypeChange = (measureType) => {
    const meta = getMeasureTypeMeta(measureType);
    const nextUnits = meta?.units?.length ? [...meta.units] : [];
    setForm((prev) => ({
      ...prev,
      measureType,
      units: nextUnits,
      defaultUnit: nextUnits[0] || "",
      measureDirection:
        meta?.defaultDirection || MEASURE_DIRECTIONS.DESCENDING,
      customUnitsText: measureType === "custom" ? prev.customUnitsText : "",
    }));
    setErrors((prev) => ({
      ...prev,
      measureType: "",
      units: "",
      defaultUnit: "",
    }));
  };

  const toggleUnit = (unit) => {
    setForm((prev) => {
      const exists = prev.units.includes(unit);
      const units = exists
        ? prev.units.filter((item) => item !== unit)
        : [...prev.units, unit];
      const defaultUnit = units.includes(prev.defaultUnit)
        ? prev.defaultUnit
        : units[0] || "";
      return { ...prev, units, defaultUnit };
    });
    setErrors((prev) => ({ ...prev, units: "", defaultUnit: "" }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.label?.trim()) nextErrors.label = "Question is required";
    if (!form.mode) nextErrors.mode = "Select a question type";

    if (
      form.mode === QUESTION_MODES.GENERAL ||
      form.mode === QUESTION_MODES.COACH_REVIEW
    ) {
      if (!form.fieldType) nextErrors.fieldType = "Field type is required";
      if (needsOptions(form.fieldType) && !form.optionsText.trim()) {
        nextErrors.optionsText = "Add at least one option (one per line)";
      }
      if (isScaleFieldType(form.fieldType)) {
        if (Number(form.scaleMax) <= Number(form.scaleMin)) {
          nextErrors.scaleMax = "Max must be greater than min";
        }
      }
    }

    if (form.mode === QUESTION_MODES.MEASURABLE) {
      if (!form.measureType) nextErrors.measureType = "Select a measure type";
      if (!form.units.length) nextErrors.units = "Select at least one unit";
      if (!form.defaultUnit) nextErrors.defaultUnit = "Select a default unit";
      if (!form.measureDirection) {
        nextErrors.measureDirection = "Select ascending or descending";
      }
      if (form.measureType === "custom" && !customUnits.length) {
        nextErrors.customUnitsText = "Add at least one custom unit";
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate() || isSubmitting) return;

    const isMeasurable = form.mode === QUESTION_MODES.MEASURABLE;
    const isFieldBased =
      form.mode === QUESTION_MODES.GENERAL ||
      form.mode === QUESTION_MODES.COACH_REVIEW;
    const isScale = isFieldBased && isScaleFieldType(form.fieldType);

    const options = isScale
      ? getScaleOptions(form.scaleMin, form.scaleMax)
      : form.optionsText
          .split("\n")
          .map((item) => item.trim())
          .filter(Boolean);

    const base = question || createEmptyQuestion();
    const qId = question?._id || question?.id;

    const payload = {
      label: form.label.trim(),
      placeholder: form.placeholder.trim(),
      fieldType: isMeasurable ? "number" : form.fieldType,
      mode: form.mode,
      measureType: isMeasurable ? form.measureType : "",
      units: isMeasurable ? form.units : [],
      defaultUnit: isMeasurable ? form.defaultUnit : "",
      customUnits: isMeasurable ? customUnits : [],
      measureDirection: isMeasurable
        ? form.measureDirection
        : MEASURE_DIRECTIONS.DESCENDING,
      scaleMin: isScale ? Number(form.scaleMin) || 1 : 1,
      scaleMax: isScale ? Number(form.scaleMax) || 10 : 10,
      options:
        isFieldBased && (needsOptions(form.fieldType) || isScale)
          ? options
          : [],
      status: base.status || "active",
    };

    setIsSubmitting(true);
    try {
      let saved;
      if (qId) {
        const res = await updateTrackerQuestionApi(qId, payload);
        if (res?.code === 200 && res?.question) {
          saved = normalizeQuestion(res.question);
        } else {
          saved = saveQuestion({ ...base, ...payload, id: qId, _id: qId });
        }
        enqueueSnackbar(res?.message || "Question updated", {
          variant: "success",
        });
      } else {
        const res = await addTrackerQuestionApi(payload);
        if (res?.code === 200 && res?.question) {
          saved = normalizeQuestion(res.question);
        } else {
          saved = saveQuestion({ ...base, ...payload });
        }
        enqueueSnackbar(res?.message || "Question added", {
          variant: "success",
        });
      }

      saveQuestion(saved);
      onClose();
      if (onSaved) onSaved(saved);
    } catch (err) {
      enqueueSnackbar(err?.message || "Failed to save question", {
        variant: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const showScaleControls =
    (form.mode === QUESTION_MODES.GENERAL ||
      form.mode === QUESTION_MODES.COACH_REVIEW) &&
    isScaleFieldType(form.fieldType);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        className: "tracker-publish-drawer MuiDrawer-paperAnchorRight",
        sx: {
          width: { xs: "100% !important", sm: "520px !important" },
          maxWidth: "100vw !important",
          border: "none",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#fff",
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2.25,
          borderBottom: `1px solid ${BORDER}`,
          display: "flex",
          gap: 2,
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
            <HelpOutlineIcon sx={{ color: PRIMARY, fontSize: 22 }} />
            <Typography sx={{ fontWeight: 700, fontSize: 20, color: "#0f172a" }}>
              {isEdit ? "Edit Question" : "Add Question"}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 13, color: MUTED, lineHeight: 1.5 }}>
            {isEdit
              ? "Update this shared question used across tracker forms."
              : "Create a shared question for submissions, measurable fields, or coach review."}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          px: 3,
          py: 2.5,
        }}
      >
        <div className="row">
          <div className="col-12 mb-3">
            <TextField
              select
              fullWidth
              size="small"
              required
              label="Question Type"
              value={form.mode}
              onChange={(e) => handleModeChange(e.target.value)}
              error={Boolean(errors.mode)}
              helperText={errors.mode || " "}
            >
              <MenuItem value={QUESTION_MODES.GENERAL}>
                General (Submissions)
              </MenuItem>
              <MenuItem value={QUESTION_MODES.MEASURABLE}>Measurable</MenuItem>
              <MenuItem value={QUESTION_MODES.COACH_REVIEW}>
                Coach review
              </MenuItem>
            </TextField>
          </div>

          <div className="col-12 mb-3">
            <TextField
              fullWidth
              size="small"
              required
              label="Question"
              value={form.label}
              onChange={(e) => handleChange("label", e.target.value)}
              error={Boolean(errors.label)}
              helperText={
                errors.label ||
                (form.mode === QUESTION_MODES.COACH_REVIEW
                  ? "Shown to coaches when they complete a review"
                  : " ")
              }
            />
          </div>

          <div className="col-12 mb-3">
            <TextField
              fullWidth
              size="small"
              label="Placeholder"
              value={form.placeholder}
              onChange={(e) => handleChange("placeholder", e.target.value)}
              placeholder={
                showScaleControls
                  ? "e.g. Rate from 1 to 10"
                  : "e.g. Enter your answer"
              }
              helperText=" "
            />
          </div>

          {form.mode === QUESTION_MODES.MEASURABLE ? (
            <>
              <div className="col-md-6 mb-3">
                <TextField
                  select
                  fullWidth
                  size="small"
                  required
                  label="Measure Type"
                  value={form.measureType || ""}
                  onChange={(e) => handleMeasureTypeChange(e.target.value)}
                  error={Boolean(errors.measureType)}
                  helperText={errors.measureType || " "}
                >
                  {MEASURE_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </TextField>
              </div>

              <div className="col-md-6 mb-3">
                <TextField
                  select
                  fullWidth
                  size="small"
                  required
                  label="Best Outcome"
                  value={form.measureDirection || ""}
                  onChange={(e) =>
                    handleChange("measureDirection", e.target.value)
                  }
                  error={Boolean(errors.measureDirection)}
                  helperText={errors.measureDirection || " "}
                >
                  <MenuItem value={MEASURE_DIRECTIONS.ASCENDING}>
                    Ascending (lower is better)
                  </MenuItem>
                  <MenuItem value={MEASURE_DIRECTIONS.DESCENDING}>
                    Descending (higher is better)
                  </MenuItem>
                </TextField>
              </div>

              <div className="col-12 mb-3">
                <Typography
                  variant="body2"
                  sx={{ mb: 1, fontWeight: 600, fontSize: 13 }}
                >
                  Units (select one or more)
                </Typography>
                <div>
                  {availableUnits.map((unit) => (
                    <UnitChip
                      key={unit}
                      label={unit}
                      selected={form.units.includes(unit)}
                      onClick={() => toggleUnit(unit)}
                    />
                  ))}
                  {!availableUnits.length && (
                    <Typography variant="body2" color="text.secondary">
                      Add custom units below.
                    </Typography>
                  )}
                </div>
                {errors.units && (
                  <FormHelperText error sx={{ mt: 0.75 }}>
                    {errors.units}
                  </FormHelperText>
                )}
              </div>

              <div className="col-md-6 mb-3">
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  rows={3}
                  label="Custom Units"
                  placeholder={"One unit per line"}
                  value={form.customUnitsText}
                  onChange={(e) => handleCustomUnitsChange(e.target.value)}
                  error={Boolean(errors.customUnitsText)}
                  helperText={
                    errors.customUnitsText ||
                    "Optional extra units, or required for Custom"
                  }
                />
              </div>

              <div className="col-md-6 mb-3">
                <TextField
                  select
                  fullWidth
                  size="small"
                  required
                  label="Default Unit"
                  value={form.defaultUnit || ""}
                  onChange={(e) => handleChange("defaultUnit", e.target.value)}
                  error={Boolean(errors.defaultUnit)}
                  helperText={errors.defaultUnit || " "}
                  disabled={!form.units.length}
                >
                  {form.units.map((unit) => (
                    <MenuItem key={unit} value={unit}>
                      {unit}
                    </MenuItem>
                  ))}
                </TextField>
              </div>
            </>
          ) : (
            <>
              <div className="col-12 mb-3">
                <TextField
                  select
                  fullWidth
                  size="small"
                  required
                  label="Field Type"
                  value={form.fieldType}
                  onChange={(e) => handleFieldTypeChange(e.target.value)}
                  error={Boolean(errors.fieldType)}
                  helperText={errors.fieldType || " "}
                >
                  {QUESTION_FIELD_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </TextField>
              </div>

              {showScaleControls ? (
                <>
                  <div className="col-md-6 mb-3">
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Scale from"
                      value={form.scaleMin}
                      onChange={(e) =>
                        handleScaleRangeChange("scaleMin", e.target.value)
                      }
                      inputProps={{ min: 0, max: 100 }}
                      error={Boolean(errors.scaleMin)}
                      helperText={errors.scaleMin || " "}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Scale to"
                      value={form.scaleMax}
                      onChange={(e) =>
                        handleScaleRangeChange("scaleMax", e.target.value)
                      }
                      inputProps={{ min: 1, max: 100 }}
                      error={Boolean(errors.scaleMax)}
                      helperText={errors.scaleMax || " "}
                    />
                  </div>
                </>
              ) : null}

              {needsOptions(form.fieldType) && (
                <div className="col-12 mb-3">
                  <TextField
                    fullWidth
                    size="small"
                    required
                    multiline
                    rows={4}
                    label="Options"
                    placeholder="One option per line"
                    value={form.optionsText}
                    onChange={(e) =>
                      handleChange("optionsText", e.target.value)
                    }
                    error={Boolean(errors.optionsText)}
                    helperText={errors.optionsText || "One option per line"}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </Box>

      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${BORDER}`,
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.25,
          flexShrink: 0,
          bgcolor: "#fff",
        }}
      >
        <button
          type="button"
          className="small-contained-button"
          onClick={onClose}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className="small-contained-button"
          onClick={handleSave}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Saving…" : isEdit ? "Update" : "Save"}
        </button>
      </Box>
    </Drawer>
  );
}
